import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, Upload, FileCheck2, X, Check, ArrowRight, ArrowLeft, ShieldCheck, AlertTriangle } from "lucide-react";

const empty = { reg: "", make: "", model: "", year: "", variant: "", mileage: "", price: "", colour: "", fuel: "", transmission: "", body_type: "", description: "", location: "", postcode: "", power_bhp: "", engine_cc: "", condition: "", lookup_last_mileage: null, lookup_conflict: false };

export default function CreateListing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [reg, setReg] = useState("");
  const [looking, setLooking] = useState(false);
  const [form, setForm] = useState(empty);
  const [listingId, setListingId] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [v5cDone, setV5cDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (!user?.seller_type) { navigate("/onboard"); return; }
    api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {});
  }, [user, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doLookup = async () => {
    if (!reg.trim()) return;
    setLooking(true);
    try {
      const { data } = await api.get(`/reg-lookup/${encodeURIComponent(reg.trim())}`);
      setForm((f) => ({
        ...f, reg: data.reg, make: data.make, model: data.model, year: data.year, variant: data.variant,
        colour: data.colour, fuel: data.fuel, transmission: data.transmission, body_type: data.body_type, power_bhp: data.power_bhp || "", engine_cc: data.engine_cc || "",
        lookup_last_mileage: data.last_mot_mileage, lookup_conflict: data.conflict,
      }));
      if (data.conflict) toast.warning("Vehicle data conflict detected — this listing will go to manual review.");
      else toast.success(`Found: ${data.make} ${data.model} (${data.year})`);
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLooking(false); }
  };

  const skipLookup = () => { setForm(empty); setStep(2); };

  const ensureDraft = async () => {
    if (listingId) {
      await api.put(`/listings/${listingId}`, payload());
      return listingId;
    }
    const { data } = await api.post("/listings", payload());
    setListingId(data.id);
    return data.id;
  };

  const payload = () => ({
    reg: form.reg || null, make: form.make, model: form.model, year: Number(form.year),
    variant: form.variant || null, mileage: Number(form.mileage), price: Number(form.price),
    colour: form.colour || null, fuel: form.fuel || null, transmission: form.transmission || null,
    body_type: form.body_type || null, description: form.description || null, location: form.location || null,
    postcode: form.postcode || null, power_bhp: form.power_bhp ? Number(form.power_bhp) : null,
    engine_cc: form.engine_cc ? Number(form.engine_cc) : null, condition: form.condition || null,
    lookup_last_mileage: form.lookup_last_mileage, lookup_conflict: form.lookup_conflict,
  });

  const goToMedia = async () => {
    if (!form.make || !form.model || !form.year || !form.mileage || !form.price || !form.postcode) { toast.error("Fill make, model, year, mileage, price and postcode."); return; }
    setSaving(true);
    try { await ensureDraft(); setStep(3); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const id = await ensureDraft();
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try { const { data } = await api.post(`/listings/${id}/photos`, fd); setPhotos((p) => [...p, data.path]); }
      catch (err) { toast.error("Photo upload failed"); }
    }
    toast.success("Photos uploaded");
  };

  const uploadV5c = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const id = await ensureDraft();
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/listings/${id}/v5c`, fd); setV5cDone(true); toast.success("V5C uploaded to private storage"); }
    catch (err) { toast.error("V5C upload failed"); }
  };

  const publish = async () => {
    setSaving(true);
    try {
      const id = await ensureDraft();
      const { data } = await api.post(`/listings/${id}/submit`);
      if (data.status === "live") toast.success("Your car is live!");
      else toast.info("Submitted — your listing is pending review.");
      navigate("/dashboard");
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail);
      if (err.response?.status === 402) { toast.error(detail); navigate(user?.seller_type === "dealer" ? "/pricing/dealers" : "/pricing/private"); }
      else toast.error(detail);
    } finally { setSaving(false); }
  };

  const entitled = sub && (sub.active || sub.listing_credits > 0);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-heading text-3xl font-bold">Sell your car</h1>
      <div className="flex items-center gap-2 mt-6 mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= s ? "bg-accent text-white" : "bg-muted text-muted-foreground"}`}>{step > s ? <Check className="w-4 h-4" /> : s}</div>
            {s < 3 && <div className={`flex-1 h-0.5 mx-2 ${step > s ? "bg-accent" : "bg-muted"}`} />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 animate-fade-up">
          <h2 className="font-heading text-xl font-semibold">Start with your registration</h2>
          <p className="text-muted-foreground text-sm mt-1">We'll pull the make, model and spec automatically.</p>
          <div className="flex items-end gap-3 mt-6 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label>Number plate</Label>
              <Input data-testid="reg-input" value={reg} onChange={(e) => setReg(e.target.value.toUpperCase())} placeholder="e.g. AB12 CDE" className="mt-1 h-12 uk-plate text-lg px-4" />
            </div>
            <Button onClick={doLookup} disabled={looking} className="h-12 px-6 bg-primary text-white" data-testid="reg-lookup-btn">
              {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" />Look up</>}
            </Button>
          </div>
          <button onClick={skipLookup} className="text-sm text-accent mt-4 font-medium" data-testid="skip-lookup-btn">No plate? Enter details manually →</button>
        </div>
      )}

      {step === 2 && (
        <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 animate-fade-up space-y-4">
          <h2 className="font-heading text-xl font-semibold">Vehicle details</h2>
          {form.lookup_conflict && (
            <div className="flex gap-2 text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> The looked-up data conflicts with the plate. Your listing will be manually reviewed before going live.
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Make</Label><Input data-testid="form-make" value={form.make} onChange={(e) => set("make", e.target.value)} className="mt-1" /></div>
            <div><Label>Model</Label><Input data-testid="form-model" value={form.model} onChange={(e) => set("model", e.target.value)} className="mt-1" /></div>
            <div><Label>Year</Label><Input data-testid="form-year" type="number" value={form.year} onChange={(e) => set("year", e.target.value)} className="mt-1" /></div>
            <div><Label>Variant / trim</Label><Input data-testid="form-variant" value={form.variant} onChange={(e) => set("variant", e.target.value)} className="mt-1" /></div>
            <div><Label>Mileage</Label><Input data-testid="form-mileage" type="number" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} className="mt-1" /></div>
            <div><Label>Price (£)</Label><Input data-testid="form-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1" /></div>
            <div><Label>Fuel</Label>
              <Select value={form.fuel || undefined} onValueChange={(v) => set("fuel", v)}><SelectTrigger className="mt-1" data-testid="form-fuel"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{["Petrol", "Diesel", "Electric", "Hybrid"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Gearbox</Label>
              <Select value={form.transmission || undefined} onValueChange={(v) => set("transmission", v)}><SelectTrigger className="mt-1" data-testid="form-transmission"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{["Manual", "Automatic"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Colour</Label><Input data-testid="form-colour" value={form.colour} onChange={(e) => set("colour", e.target.value)} className="mt-1" /></div>
            <div><Label>Body type</Label>
              <Select value={form.body_type || undefined} onValueChange={(v) => set("body_type", v)}><SelectTrigger className="mt-1" data-testid="form-body-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{["Hatchback", "Saloon", "Estate", "SUV", "Coupe", "Convertible", "MPV", "Pickup", "Van"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Engine size (cc)</Label><Input data-testid="form-engine-cc" type="number" value={form.engine_cc} onChange={(e) => set("engine_cc", e.target.value)} className="mt-1" /></div>
            <div><Label>Condition</Label>
              <Select value={form.condition || undefined} onValueChange={(v) => set("condition", v)}><SelectTrigger className="mt-1" data-testid="form-condition"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{["Excellent", "Good", "Fair", "Needs work"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Power (bhp)</Label><Input data-testid="form-power" type="number" value={form.power_bhp} onChange={(e) => set("power_bhp", e.target.value)} className="mt-1" /></div>
            <div><Label>Postcode</Label><Input data-testid="form-postcode" value={form.postcode} onChange={(e) => set("postcode", e.target.value.toUpperCase())} className="mt-1" placeholder="e.g. M1 1AE" /></div>
            <div><Label>Town (optional)</Label><Input data-testid="form-location" value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1" placeholder="Filled from postcode" /></div>
          </div>
          <div><Label>Description</Label><Textarea data-testid="form-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="mt-1" placeholder="Service history, condition, extras…" /></div>
          <div className="flex justify-between pt-2">
            <Button variant="ghost" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <Button onClick={goToMedia} disabled={saving} className="bg-primary text-white" data-testid="to-media-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue<ArrowRight className="w-4 h-4 ml-1" /></>}</Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 animate-fade-up space-y-6">
          <div>
            <h2 className="font-heading text-xl font-semibold">Photos</h2>
            <p className="text-muted-foreground text-sm">Add at least one clear photo. More photos sell faster.</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-border">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
              <label className="aspect-square rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-accent text-muted-foreground" data-testid="photo-upload-label">
                <Upload className="w-6 h-6" /><span className="text-xs mt-1">Add</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhoto} data-testid="photo-upload-input" />
              </label>
            </div>
          </div>

          <div>
            <h2 className="font-heading text-xl font-semibold">V5C document <span className="text-sm font-normal text-muted-foreground">(optional)</span></h2>
            <p className="text-muted-foreground text-sm">Upload your logbook to earn a Verified badge after review. Stored privately.</p>
            <label className={`mt-3 flex items-center gap-3 p-4 rounded-lg border-2 border-dashed cursor-pointer ${v5cDone ? "border-emerald-400 bg-emerald-50" : "border-border hover:border-accent"}`} data-testid="v5c-upload-label">
              {v5cDone ? <FileCheck2 className="w-5 h-5 text-emerald-600" /> : <Upload className="w-5 h-5 text-muted-foreground" />}
              <span className="text-sm">{v5cDone ? "V5C uploaded — pending review" : "Upload V5C (PDF or image)"}</span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={uploadV5c} data-testid="v5c-upload-input" />
            </label>
          </div>

          {!entitled && (
            <div className="flex gap-2 text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-lg p-3">
              <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" /> You'll need an active plan or listing credit to publish. You can choose one at checkout.
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="ghost" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <Button onClick={publish} disabled={saving || photos.length === 0} className="bg-accent hover:bg-emerald-600 text-white h-11 px-6" data-testid="publish-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publish listing"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
